import type { ViewerShell } from '../../shell';
import { confirmDialog } from '../../ui/dialogs';
import { RisError } from '../client';
import type { AssistantApi } from './api';
import { conversationStudy } from './context';
import { askErrorText } from './panel';
import type { ReportPart } from './radiologist/logic';
import { sendLabel, sendToReport } from './radiologist/send';
import { reportSendMode } from './radiologist/switches';
import { REPORT_SECTION_KEYS, REPORT_SYSTEMS, type ReportSectionKey, type ReportSystem, type StructuredReport } from './shapes';

/**
 * "Informe estructurado" (contract v2 §4): pick a system (BI-RADS, Lung-RADS…), ris writes a draft
 * from the ACCEPTED findings and the org's protocols, the card shows the category, the sections and
 * the guideline citations. Copying goes through the same confirmation as round 1: the whole text is
 * shown before anything reaches the clipboard, or (radiologist tools §2.1, "Enviar al informe") the
 * reader's draft. The viewer never inserts it into a report by itself.
 */

export const REPORT_TEXT = {
  tool: 'Informe estructurado',
  title: 'Informe estructurado sugerido',
  system: 'Sistema',
  generate: 'Generar',
  generating: 'Generando…',
  category: 'Categoría',
  citations: 'Referencias',
  copyTitle: 'Copiar al informe',
  copyIntro: 'Revise el texto sugerido antes de copiarlo. Se copiará exactamente esto:',
  copyConfirm: 'Copiar',
  copyCancel: 'Cancelar',
  copied: 'Texto copiado para el informe.',
  copyFailed: 'No se pudo copiar el texto.',
  empty: 'El Asistente Guru no devolvió un informe estructurado.',
  needsAccepted: 'El informe estructurado se redacta con los hallazgos aceptados: acepte o corrija al menos uno.',
  model: 'Modelo',
  disclaimer: 'Borrador sugerido · no firma · no decide',
} as const;

const SYSTEM_LABELS: Record<ReportSystem, string> = {
  auto: 'Automático',
  'BI-RADS': 'BI-RADS',
  'Lung-RADS': 'Lung-RADS',
  'TI-RADS': 'TI-RADS',
  'LI-RADS': 'LI-RADS',
};

const SECTION_LABELS: Record<ReportSectionKey, string> = {
  findings: 'Hallazgos',
  impression: 'Impresión',
  recommendation: 'Recomendación',
};

/** ris answers 409 / 422 when there is nothing accepted to write from. */
const NOTHING_ACCEPTED = new Set([409, 422]);

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** The plain text "Copiar" puts on the clipboard (and the confirmation shows in full). */
export function reportClipboardText(report: StructuredReport): string {
  const lines: string[] = [];
  const head = [report.system, report.category].filter(Boolean).join(' ');
  if (head) lines.push(`${REPORT_TEXT.category}: ${head}`);
  for (const key of REPORT_SECTION_KEYS) {
    const text = report.sections[key];
    if (text) lines.push(`${SECTION_LABELS[key]}: ${text}`);
  }
  if (report.citations.length > 0) {
    lines.push(`${REPORT_TEXT.citations}:`);
    report.citations.forEach((c) => lines.push(`- ${[c.guideline, c.text].filter(Boolean).join(': ')}`));
  }
  return lines.join('\n');
}

/**
 * What "Enviar al informe" appends, section by section: the category opens the impression, the
 * recommendation and the citations go to Recomendaciones.
 */
export function reportParts(report: StructuredReport): ReportPart[] {
  const head = [report.system, report.category].filter(Boolean).join(' ');
  const impression = [head ? `${REPORT_TEXT.category}: ${head}` : '', report.sections.impression ?? ''].filter(Boolean).join('\n');
  const citations = report.citations.map((c) => `- ${[c.guideline, c.text].filter(Boolean).join(': ')}`);
  const recommendations = [report.sections.recommendation ?? '', citations.length > 0 ? `${REPORT_TEXT.citations}:\n${citations.join('\n')}` : '']
    .filter(Boolean).join('\n');
  return [
    { section: 'findings', text: report.sections.findings ?? '' },
    { section: 'impression', text: impression },
    { section: 'recommendations', text: recommendations },
  ];
}

function renderReport(report: StructuredReport, onCopy: () => void): HTMLElement {
  const out = el('div', 'assistant-report-result');
  const head = [report.system, report.category].filter(Boolean).join(' · ');
  if (head) out.appendChild(el('div', 'assistant-report-category', `${REPORT_TEXT.category}: ${head}`));
  for (const key of REPORT_SECTION_KEYS) {
    const text = report.sections[key];
    if (!text) continue;
    const section = el('div', `assistant-report-section section-${key}`);
    section.append(el('div', 'assistant-report-section-title', SECTION_LABELS[key]), el('div', 'assistant-report-section-text', text));
    out.appendChild(section);
  }
  if (report.citations.length > 0) {
    out.appendChild(el('div', 'assistant-report-section-title', REPORT_TEXT.citations));
    const list = el('ul', 'assistant-report-citations');
    report.citations.forEach((c) => list.appendChild(el('li', 'assistant-report-citation', [c.guideline, c.text].filter(Boolean).join(': '))));
    out.appendChild(list);
  }
  if (report.model) out.appendChild(el('div', 'assistant-trace', `${REPORT_TEXT.model}: ${report.model}`));
  const copy = el('button', 'assistant-btn assistant-report-copy', sendLabel());
  copy.type = 'button';
  copy.addEventListener('click', onCopy);
  out.appendChild(copy);
  return out;
}

async function copyReport(shell: ViewerShell, report: StructuredReport): Promise<void> {
  const text = reportClipboardText(report);
  const confirmed = await confirmDialog(shell, {
    title: REPORT_TEXT.copyTitle, text: `${REPORT_TEXT.copyIntro}\n\n${text}`,
    confirmLabel: REPORT_TEXT.copyConfirm, cancelLabel: REPORT_TEXT.copyCancel, className: 'assistant-insert-dialog assistant-report-dialog',
  });
  if (!confirmed) return;
  try {
    await navigator.clipboard.writeText(text);
    shell.toast(REPORT_TEXT.copied);
  } catch (error) {
    console.warn('[viewer] clipboard write failed', error);
    shell.toast(REPORT_TEXT.copyFailed, 'error');
  }
}

function reportErrorText(error: unknown): string {
  if (error instanceof RisError && NOTHING_ACCEPTED.has(error.status)) return REPORT_TEXT.needsAccepted;
  return askErrorText(error);
}

function systemPicker(): HTMLSelectElement {
  const select = el('select', 'assistant-report-system');
  select.id = `assistant-report-system-${Math.random().toString(36).slice(2, 8)}`;
  for (const system of REPORT_SYSTEMS) {
    const option = el('option', '', SYSTEM_LABELS[system]);
    option.value = system;
    select.appendChild(option);
  }
  return select;
}

/** The card the "Informe estructurado" tool adds to the conversation. */
export function structuredReportCard(shell: ViewerShell, api: AssistantApi): HTMLElement {
  const card = el('div', 'assistant-report-card');
  card.appendChild(el('div', 'assistant-analysis-title', REPORT_TEXT.title));
  card.appendChild(el('div', 'assistant-trace', REPORT_TEXT.disclaimer));
  const form = el('form', 'assistant-report-form');
  const select = systemPicker();
  const label = el('label', 'assistant-report-label', REPORT_TEXT.system);
  label.htmlFor = select.id;
  const generate = el('button', 'assistant-btn assistant-report-generate', REPORT_TEXT.generate);
  generate.type = 'submit';
  form.append(label, select, generate);
  const result = el('div', 'assistant-report-output');
  result.setAttribute('aria-live', 'polite');
  card.append(form, result);
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void generateInto(shell, api, select.value as ReportSystem, { generate, result });
  });
  return card;
}

async function generateInto(shell: ViewerShell, api: AssistantApi, system: ReportSystem, ui: { generate: HTMLButtonElement; result: HTMLElement }): Promise<void> {
  if (ui.generate.disabled) return;
  ui.generate.disabled = true;
  ui.generate.textContent = REPORT_TEXT.generating;
  try {
    const study = conversationStudy(shell);
    const report = await api.structuredReport(study, system);
    const onCopy = (r: StructuredReport) => (reportSendMode() === 'send' ? void sendToReport(shell, api, reportParts(r), study) : void copyReport(shell, r));
    ui.result.replaceChildren(report ? renderReport(report, () => onCopy(report)) : el('p', 'assistant-empty', REPORT_TEXT.empty));
  } catch (error) {
    console.warn('[viewer] structured report failed', error);
    shell.toast(reportErrorText(error), 'error');
  } finally {
    ui.generate.disabled = false;
    ui.generate.textContent = REPORT_TEXT.generate;
  }
}
